/**
 * foldAccess — fold the team-access agent's `access_commands` blocks across a
 * session's messages into the CURRENT proposal per target (latest-writer-wins),
 * mirroring foldAllocation's grain. The Team board renders these as
 * AccessProposalCard side-channel proposals the lead confirms with one tap;
 * access truth comes from the location roster read + the ACL grant the
 * confirm writes, never from the fold.
 */

import type { MessageResponse } from '@/api/types_session';

export const ACCESS_COMMANDS_BLOCK_TYPE = 'access_commands';

/** The verbs the agent can propose. Approve/deny resolve an existing request. */
export type AccessCommandAction = 'grant' | 'revoke' | 'approve' | 'deny';

const ACCESS_COMMAND_ACTIONS = new Set<string>(['grant', 'revoke', 'approve', 'deny']);

/**
 * One proposed access change. Structure only — the connected layer resolves the
 * principal against the roster and executes via the SAME REST endpoints as the
 * board's own grant/decide/revoke.
 */
export interface AccessCommand {
  action: AccessCommandAction;
  /** Who the access is for (user or role group). Absent on some approve/deny. */
  principal?: { type: string; id: string };
  /** What the access is on. Absent on some approve/deny (the request carries it). */
  resource?: { type: string; id: string; label?: string };
  /** Requested/target role, e.g. 'viewer' | 'editor'. */
  role?: string;
  /** ISO-8601 — set when the proposed grant self-expires. */
  expires_at?: string;
  /** Set for approve/deny that resolve an existing access request. */
  request_id?: string;
}

/** Runtime narrowing for the wire block payload. */
export function isAccessCommands(data: unknown): data is { commands: AccessCommand[] } {
  if (typeof data !== 'object' || data === null) return false;
  const cmds = (data as { commands?: unknown }).commands;
  return (
    Array.isArray(cmds) &&
    cmds.every(
      (c) =>
        typeof c === 'object' &&
        c !== null &&
        ACCESS_COMMAND_ACTIONS.has((c as AccessCommand).action),
    )
  );
}

/**
 * Latest proposal per target across the whole session — EMPTY from the
 * session cut-over on: the access_commands block kind has no counterpart in
 * the conversation payload upstream (text / tool / citation blocks), so there
 * is nothing to fold until the access commands get a contract (fabric
 * directives are the candidate). The board's access truth was always the
 * location roster read + the ACL grants, which are unaffected
 * (foldAllocation precedent).
 */
export function foldAccess(_messages: MessageResponse[] | undefined): AccessCommand[] {
  return []
}
